import type { CreateEngineerBody, Engineer } from '@lib/api-types/engineers';
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ApiError } from '../api/client.ts';
import { useAssignEngineerTeam, useTeams, type Team } from '../api.ts';
import { OrgPicker } from '../components/OrgPicker.tsx';
import { Select } from '../components/Select.tsx';
import {
  useCreateEngineer,
  useDeleteEngineer,
  useDepartEngineer,
  useEngineers,
  useMergeEngineers,
  useRestoreEngineer,
  useUpdateEngineer,
} from '../view-models/engineers.ts';

import type { SettingsOutletContext } from './Settings.tsx';
import { TeamsManager, currentTeamId } from './TeamsManager.tsx';

/**
 * Engineers section of /settings/setup — second consumer of the new typed API.
 * CRUD + depart for engineers; ratings management lives separately
 * (the existing Talent matrix is the better UI for that).
 */
export function SettingsEngineers() {
  const { orgSlug, role } = useOutletContext<SettingsOutletContext>();
  const list = useEngineers(orgSlug);
  const canMutate = role === 'admin';
  // Departed engineers are excluded from the active list above; fetch them
  // separately for the restore panel (admins only) and to seed slug dedup.
  const withDeparted = useEngineers(canMutate ? orgSlug : null, { includeDeparted: true });
  const teams = useTeams(orgSlug);
  const teamList = teams.data ?? [];
  const [adding, setAdding] = useState(false);

  const allEngineers = withDeparted.data ?? list.data ?? [];
  const forReview = allEngineers.filter((e) => e.status === 'review');
  const departed = allEngineers.filter((e) => e.status === 'departed');
  const existingIds = new Set(allEngineers.map((e) => e.id));

  return (
    <div className="stack" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      <div style={{ marginBottom: '-1rem' }}>
        <OrgPicker />
      </div>

      {canMutate && !teams.isPending && !teams.isError ? (
        <TeamsManager orgSlug={orgSlug} teams={teamList} engineers={list.data ?? []} />
      ) : null}

      <section>
        <h2 className="settings-h2" style={{ marginBottom: '0.25rem' }}>
          Engineers
        </h2>
        {canMutate ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setAdding(true)}
            style={{ marginTop: '0.25rem', marginBottom: '0.75rem' }}
          >
            Add engineer
          </button>
        ) : null}
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          The people (and agents) on the team. Engineers show up as columns
          in the Talent matrix and as candidate sources of coverage in
          Resilience.
        </p>
        {list.isPending ? (
          <p className="muted">Loading…</p>
        ) : list.isError ? (
          <ErrorBanner error={list.error} />
        ) : (
          <EngineersList
            items={list.data}
            teams={teamList}
            orgSlug={orgSlug}
            canMutate={canMutate}
          />
        )}
        {canMutate ? (
          <>
            <CollapsibleEngineerSection
              title="For review"
              engineers={forReview}
              renderRow={(e) => <ReviewRow key={e.id} eng={e} orgSlug={orgSlug} />}
            />
            <CollapsibleEngineerSection
              title="Departed engineers"
              engineers={departed}
              renderRow={(e) => <DepartedRow key={e.id} eng={e} orgSlug={orgSlug} />}
            />
          </>
        ) : null}
      </section>
      {!canMutate ? (
        <p className="muted">Only org owners can add or modify engineers.</p>
      ) : null}

      {adding ? (
        <AddEngineerModal
          orgSlug={orgSlug}
          existingIds={existingIds}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  );
}

function EngineersList({
  items,
  teams,
  orgSlug,
  canMutate,
}: {
  items: Engineer[];
  teams: Team[];
  orgSlug: string;
  canMutate: boolean;
}) {
  if (items.length === 0) return <p className="muted">No engineers yet.</p>;
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {items.map((e) => (
        <EngineerRow
          key={e.id}
          eng={e}
          teams={teams}
          orgSlug={orgSlug}
          canMutate={canMutate}
          allEngineers={items}
        />
      ))}
    </ul>
  );
}

function EngineerRow({
  eng,
  teams,
  orgSlug,
  canMutate,
  allEngineers,
}: {
  eng: Engineer;
  teams: Team[];
  orgSlug: string;
  canMutate: boolean;
  allEngineers: Engineer[];
}) {
  const [editing, setEditing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [displayName, setDisplayName] = useState(eng.display_name);
  const [primaryEmail, setPrimaryEmail] = useState(eng.primary_email ?? '');
  const update = useUpdateEngineer(orgSlug);
  const depart = useDepartEngineer(orgSlug);
  const del = useDeleteEngineer(orgSlug);
  const merge = useMergeEngineers(orgSlug);
  const assignTeam = useAssignEngineerTeam(orgSlug);
  const teamId = currentTeamId(eng.id, teams);
  const teamName = teamId === null ? 'Unassigned' : (teams.find((t) => t.id === teamId)?.name ?? 'Unassigned');

  // Merge: this row is the duplicate being folded into `mergeTarget`.
  const otherEngineers = allEngineers.filter((o) => o.id !== eng.id);
  const mergeTarget = allEngineers.find((o) => o.id === mergeTargetId) ?? null;
  const mergedEmails = mergeTarget
    ? [...new Set([...mergeTarget.git_emails, ...eng.git_emails])]
    : [];
  const mergedLogins = mergeTarget
    ? [...new Set([...mergeTarget.github_logins, ...eng.github_logins])]
    : [];

  if (editing) {
    return (
      <li style={liStyle}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate(
              {
                id: eng.id,
                body: {
                  display_name: displayName,
                  primary_email: primaryEmail.trim().length > 0 ? primaryEmail : null,
                },
              },
              { onSuccess: () => setEditing(false) },
            );
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <label>
            <span style={labelStyle}>Display name</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label>
            <span style={labelStyle}>Primary email</span>
            <input
              type="email"
              value={primaryEmail}
              onChange={(e) => setPrimaryEmail(e.target.value)}
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={update.isPending}>{update.isPending ? 'Saving…' : 'Save'}</button>
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            {update.isError ? <ErrorBanner error={update.error} /> : null}
          </div>
        </form>
      </li>
    );
  }

  return (
    <li style={{ ...liStyle, opacity: eng.status === 'departed' ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <strong>{eng.display_name}</strong>{' '}
          <code style={{ color: 'var(--color-muted, #888)' }}>{eng.id}</code>{' '}
          {eng.status !== 'active' ? (
            <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: '#888' }}>({eng.status})</span>
          ) : null}
          <div className="muted" style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
            {eng.primary_email ?? '—'} · {eng.kind} · {eng.scope} · {teamName}
          </div>
        </div>
        {canMutate ? (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Select
              value={teamId === null ? '' : String(teamId)}
              onChange={(e) =>
                assignTeam.mutate({
                  engineerId: eng.id,
                  teamId: e.target.value === '' ? null : Number(e.target.value),
                })
              }
              aria-label={`Team for ${eng.display_name}`}
            >
              <option value="">Unassigned</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
            <button type="button" onClick={() => { setDisplayName(eng.display_name); setPrimaryEmail(eng.primary_email ?? ''); setEditing(true); }}>
              Edit
            </button>
            {eng.status === 'active' ? (
              <button
                type="button"
                disabled={depart.isPending}
                onClick={() => {
                  if (confirm(`Mark "${eng.display_name}" as departed?`)) {
                    depart.mutate({ id: eng.id });
                  }
                }}
              >
                {depart.isPending ? 'Departing…' : 'Depart'}
              </button>
            ) : null}
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (confirm(`Delete "${eng.display_name}"? Past matrix views still surface them. Prefer Depart for normal off-boarding.`)) {
                  del.mutate(eng.id);
                }
              }}
            >
              {del.isPending ? 'Removing…' : 'Remove'}
            </button>
            {otherEngineers.length > 0 ? (
              <button
                type="button"
                onClick={() => {
                  setMerging((m) => !m);
                  setMergeTargetId('');
                }}
              >
                {merging ? 'Close' : 'Merge…'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {merging ? (
        <div style={{ marginTop: '0.6rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          <label>
            <span style={labelStyle}>
              Merge “{eng.display_name}” into another engineer (this record is a duplicate)
            </span>
            <Select
              value={mergeTargetId}
              onChange={(e) => setMergeTargetId(e.target.value)}
              aria-label={`Merge ${eng.display_name} into`}
            >
              <option value="">Select the engineer to keep…</option>
              {otherEngineers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.display_name} ({o.id})
                </option>
              ))}
            </Select>
          </label>
          {mergeTarget ? (
            <div className="muted" style={{ fontSize: '0.85rem' }}>
              <strong>{mergeTarget.display_name}</strong> will own{' '}
              {mergedEmails.length} email{mergedEmails.length === 1 ? '' : 's'}
              {mergedEmails.length ? ` (${mergedEmails.join(', ')})` : ''} and{' '}
              {mergedLogins.length} github login{mergedLogins.length === 1 ? '' : 's'}
              {mergedLogins.length ? ` (${mergedLogins.join(', ')})` : ''}. “{eng.display_name}”
              will be removed and its history reattributed.
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              disabled={!mergeTargetId || merge.isPending}
              onClick={() =>
                merge.mutate(
                  { targetId: mergeTargetId, loserIds: [eng.id] },
                  {
                    onSuccess: () => {
                      setMerging(false);
                      setMergeTargetId('');
                    },
                  },
                )
              }
            >
              {merge.isPending ? 'Merging…' : 'Merge'}
            </button>
            <button
              type="button"
              onClick={() => {
                setMerging(false);
                setMergeTargetId('');
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
      {depart.isError ? <ErrorBanner error={depart.error} /> : null}
      {del.isError ? <ErrorBanner error={del.error} /> : null}
      {merge.isError ? <ErrorBanner error={merge.error} /> : null}
      {assignTeam.isError ? <ErrorBanner error={assignTeam.error} /> : null}
    </li>
  );
}

/**
 * Turn a human display name into a valid engineer slug (the API's `id`):
 * lowercase, alphanumerics/`_`/`-` only, must start with an alphanumeric.
 * The slug used to be user-entered but confused people, so we derive it.
 */
function slugify(name: string): string {
  const base = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 120)
    .replace(/-+$/, '');
  return base.length > 0 ? base : 'engineer';
}

/** Suffix `-2`, `-3`, … until the slug doesn't collide with an existing id. */
function uniqueSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

function AddEngineerModal({
  orgSlug,
  existingIds,
  onClose,
}: {
  orgSlug: string;
  existingIds: Set<string>;
  onClose: () => void;
}) {
  const create = useCreateEngineer(orgSlug);
  const [displayName, setDisplayName] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [kind, setKind] = useState<'human' | 'agent'>('human');

  const dirty =
    displayName.trim().length > 0 || primaryEmail.trim().length > 0 || kind !== 'human';

  const tryClose = () => {
    if (create.isPending) return;
    if (dirty && !confirm('Discard this new engineer?')) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') tryClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, create.isPending]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const name = displayName.trim();
    if (!name) return;
    const body: CreateEngineerBody = {
      id: uniqueSlug(slugify(name), existingIds),
      display_name: name,
      primary_email: primaryEmail.trim().length > 0 ? primaryEmail.trim() : null,
      kind,
    };
    create.mutate(body, { onSuccess: () => onClose() });
  };

  return (
    <div style={overlayStyle} onMouseDown={tryClose}>
      <form
        style={modalStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Add an engineer"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <h2 style={{ margin: 0, fontFamily: 'var(--display)', fontWeight: 500, fontSize: '20px' }}>
          Add an engineer
        </h2>
        <label>
          <span style={labelStyle}>Display name</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            autoFocus
            maxLength={200}
            placeholder="Alice Chen"
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Primary email (optional)</span>
          <input
            type="email"
            value={primaryEmail}
            onChange={(e) => setPrimaryEmail(e.target.value)}
            placeholder="alice@example.com"
            style={inputStyle}
          />
        </label>
        <label>
          <span style={labelStyle}>Kind</span>
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as 'human' | 'agent')}
          >
            <option value="human">Human</option>
            <option value="agent">Agent</option>
          </Select>
        </label>
        {create.isError ? <ErrorBanner error={create.error} /> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={tryClose} disabled={create.isPending}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={create.isPending}>
            {create.isPending ? 'Adding…' : 'Add engineer'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Collapsible, collapsed-by-default roster section reused for the "For review"
 * and "Departed" groups below the active engineer list. Hidden entirely when
 * the group is empty. "For review" renders above "Departed".
 */
function CollapsibleEngineerSection({
  title,
  engineers,
  defaultOpen = false,
  renderRow,
}: {
  title: string;
  engineers: Engineer[];
  defaultOpen?: boolean;
  renderRow: (eng: Engineer) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultOpen);
  if (engineers.length === 0) return null;
  return (
    <div style={{ marginTop: '1.5rem' }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={discloseStyle}
        aria-expanded={expanded}
      >
        <span style={{ display: 'inline-block', width: '1rem' }}>{expanded ? '▾' : '▸'}</span>
        {title} ({engineers.length})
      </button>
      {expanded ? (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0.5rem 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {engineers.map((e) => renderRow(e))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * A "For review" candidate — onboarding flagged this contributor as possibly
 * departed (unverifiable GitHub identity + stagnant activity) but couldn't
 * confirm. Kept OFF the talent map until an admin resolves it: "Mark active"
 * restores them to the map, "Depart" retires them.
 */
function ReviewRow({ eng, orgSlug }: { eng: Engineer; orgSlug: string }) {
  const restore = useRestoreEngineer(orgSlug);
  const depart = useDepartEngineer(orgSlug);
  const busy = restore.isPending || depart.isPending;
  return (
    <li style={{ ...liStyle }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <strong>{eng.display_name}</strong>
          <div className="muted" style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
            {eng.primary_email ?? '—'} · {eng.kind} · {eng.scope}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button type="button" disabled={busy} onClick={() => restore.mutate({ id: eng.id })}>
            {restore.isPending ? 'Marking…' : 'Mark active'}
          </button>
          <button type="button" disabled={busy} onClick={() => depart.mutate({ id: eng.id })}>
            {depart.isPending ? 'Departing…' : 'Depart'}
          </button>
        </div>
      </div>
      {restore.isError ? <ErrorBanner error={restore.error} /> : null}
      {depart.isError ? <ErrorBanner error={depart.error} /> : null}
    </li>
  );
}

function DepartedRow({ eng, orgSlug }: { eng: Engineer; orgSlug: string }) {
  const restore = useRestoreEngineer(orgSlug);
  return (
    <li style={{ ...liStyle, opacity: 0.75 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div>
          <strong>{eng.display_name}</strong>
          <div className="muted" style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
            {eng.primary_email ?? '—'} · {eng.kind} · {eng.scope}
          </div>
        </div>
        <button type="button" disabled={restore.isPending} onClick={() => restore.mutate({ id: eng.id })}>
          {restore.isPending ? 'Restoring…' : 'Restore'}
        </button>
      </div>
      {restore.isError ? <ErrorBanner error={restore.error} /> : null}
    </li>
  );
}

function ErrorBanner({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.message} (${error.status}${error.code ? ` ${error.code}` : ''})`
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <div role="alert" style={{ color: '#c33', marginTop: '0.5rem' }}>
      {message}
    </div>
  );
}

const liStyle: React.CSSProperties = {
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
  padding: '0.75rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '0.85rem',
  color: 'var(--color-muted, #666)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4rem 0.5rem',
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
};

const discloseStyle: React.CSSProperties = {
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '15px',
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  textAlign: 'left',
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalStyle: React.CSSProperties = {
  background: 'var(--card, #fff)',
  borderRadius: '10px',
  width: 'min(460px, 94vw)',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
};
